import { isAnyOf } from '@reduxjs/toolkit';
import { leaveBreadcrumb } from 'cfa-react-logging';
import { v4 } from 'uuid';

import ooeConstants from '../constants';
import { actions as diagnosticMessagesActions } from '../diagnosticMessages/reducer';
import { AppStartListening } from '../listenerMiddleware';
import {
  hasItemizedMenuPaperGoods,
  selectCombinedMenu,
  selectIndexedMenuItems,
  selectMenuPaperGoods,
} from '../menu/selectors';
import { getMenus } from '../menu/thunks';
import { setGuestCountToPaperGoodsMax } from '../menu/utils';
import { actions as orderActions } from '../order/reducers';
import {
  selectDetailsSpecialInstructions,
  selectEditMode,
  selectEditOrderId,
  selectIsEditCMTOrder,
  selectPaperGoodsBool,
  selectReorder,
} from '../order/selectors';
import { submitOrder } from '../order/thunks';
import { State } from '../reducers';
import { logEvent } from '../services/analytics';
import { getVariation } from '../services/launchDarkly';
import history from '../util/history';
import { specialInstrHasPaperGoods } from '../util/utils';
import { ensureValueIsWholeNumber } from '../util/validate';
import { actions as cartActions } from './reducer';
import { selectAllCartItems, selectPaperGoodsCartItems } from './selectors';

/**
 * Paper Goods handling for edit/reorder when menus are available
 * (same trigger points as unavailable cart-item cleanup).
 */
const handlePaperGoodsOnEdit = ({
  dispatch,
  getState,
}: {
  dispatch: (action: unknown) => void;
  getState: () => unknown;
}) => {
  const state = getState() as State;

  if ((!selectEditMode(state) && !selectReorder(state)) || selectCombinedMenu(state).length === 0) {
    return;
  }

  const showPaperGoods = getVariation('showPaperGoods', false);
  const itemizedUiAvailable = showPaperGoods && hasItemizedMenuPaperGoods(state);
  const paperGoodsCartItems = selectPaperGoodsCartItems(state);

  // Flag off / no itemized Menu: remove Paper Goods line items (they are on the menu, so
  // unavailable-item cleanup would otherwise leave them) and prompt the user.
  if (!itemizedUiAvailable && paperGoodsCartItems.length > 0) {
    leaveBreadcrumb('Paper Goods edit: itemized items unavailable', {
      reason: showPaperGoods ? 'menu_missing_paper_goods' : 'flag_off',
      tags: paperGoodsCartItems.map((item) => item.tag),
      editOrderId: selectEditOrderId(state),
    });
    dispatch(cartActions.removeManyItems({ ids: paperGoodsCartItems.map((item) => item.id) }));
    dispatch(
      diagnosticMessagesActions.addMessage(
        ooeConstants.DIAGNOSTIC_MESSAGES.PAPER_GOODS_NOT_ADDED_READD_OR_SI,
      ),
    );
    return;
  }

  // Boolean / legacy: prompt to select from Menu when the itemized experience is available.
  if (itemizedUiAvailable && paperGoodsCartItems.length === 0) {
    if (selectIsEditCMTOrder(state) && specialInstrHasPaperGoods(selectDetailsSpecialInstructions(state))) {
      dispatch(
        diagnosticMessagesActions.addMessage(
          ooeConstants.DIAGNOSTIC_MESSAGES.PAPER_GOODS_MAKE_SELECTIONS_FROM_MENU,
        ),
      );
    } else if (!selectIsEditCMTOrder(state) && selectPaperGoodsBool(state) === true) {
      dispatch(
        diagnosticMessagesActions.addMessage(ooeConstants.DIAGNOSTIC_MESSAGES.PAPER_GOODS_RESELECT_FROM_MENU),
      );
    }
  }
};

const addCartListeners = (startListening: AppStartListening) => {
  startListening({
    actionCreator: orderActions.initiateEditOrder,
    effect: (action, listenerApi) => {
      const {
        order: { lineItems },
      } = action.payload;
      const { dispatch } = listenerApi;
      dispatch(cartActions.mapLineItemsToCartItems({ lineItems }));
      handlePaperGoodsOnEdit(listenerApi);
      // Kick pricing after cart is filled so updateOrder does not race listener order / debounce.
      dispatch(orderActions.updatePricing());
    },
  });

  startListening({
    actionCreator: orderActions.exitEditOrder,
    effect: (_, listenerApi) => {
      const { dispatch } = listenerApi;
      dispatch(cartActions.clearCart());
    },
  });

  startListening({
    actionCreator: submitOrder.fulfilled,
    effect: (_, listenerApi) => {
      const { dispatch } = listenerApi;
      dispatch(cartActions.clearCart());
    },
  });

  startListening({
    matcher: isAnyOf(
      cartActions.addToCart,
      cartActions.updateQuantity,
      cartActions.deleteItem,
      cartActions.removeManyItems,
      cartActions.addModifier,
      cartActions.updateModifierQuantity,
      cartActions.updateSpecialInstructions,
      cartActions.updateSideItem,
      cartActions.updateDessertItem,
      cartActions.updatePremiumSideItem,
      cartActions.makePromoFree,
      cartActions.removePromoFree,
    ),
    effect: (action) => {
      const { type } = action;
      logEvent(type);
    },
  });

  startListening({
    actionCreator: getMenus.fulfilled,
    effect: (_, listenerApi) => {
      const { getState, dispatch } = listenerApi;
      handlePaperGoodsOnEdit(listenerApi);

      const state = getState() as State;
      const menuItems = selectIndexedMenuItems(state);
      const unavailableItemTags = selectAllCartItems(state)
        .map((item) => item.tag)
        .filter((tag) => menuItems[tag] === undefined);

      if (unavailableItemTags.length > 0) {
        dispatch(cartActions.removeNonExistentItems({ tags: unavailableItemTags }));
      }
    },
  });

  startListening({
    actionCreator: cartActions.clearAllPaperGoods,
    effect: (_, { getState, dispatch }) => {
      const state = getState() as State;
      const ids = selectPaperGoodsCartItems(state).map((item) => item.id);

      if (ids.length > 0) {
        dispatch(cartActions.removeManyItems({ ids }));
      }
    },
  });

  startListening({
    actionCreator: cartActions.updateAllPaperGoods,
    effect: (action, { getState, dispatch }) => {
      const state = getState() as State;
      const guestCount = ensureValueIsWholeNumber(setGuestCountToPaperGoodsMax(action.payload.quantity));

      if (guestCount <= 0) {
        return;
      }

      const paperGoodsCategory = selectMenuPaperGoods(state);
      const cartItemsByTag = new Map(selectPaperGoodsCartItems(state).map((item) => [item.tag, item]));

      (paperGoodsCategory?.items ?? []).forEach((item) => {
        const existingCartItem = cartItemsByTag.get(item.tag);
        const id = existingCartItem?.id || v4();

        if (!existingCartItem) {
          dispatch(cartActions.addToCart({ id, itemTag: item.tag, openCartModal: false }));
        }

        dispatch(cartActions.updateQuantity({ id, quantity: guestCount }));
      });
    },
  });

  startListening({
    actionCreator: cartActions.removeNonExistentItems,
    effect: (_, { dispatch }) => {
      dispatch(diagnosticMessagesActions.addMessage(ooeConstants.DIAGNOSTIC_MESSAGES.REMOVED_CART_ITEMS));
    },
  });

  startListening({
    actionCreator: cartActions.addToCart,
    effect: (action) => {
      const { id, openCartModal = true } = action.payload;
      const locationState = { state: { modal: openCartModal, cartItemId: id } };

      // update state if cart modal is already showing
      if (history.location.state && history.location.state.modal) {
        return history.replace(locationState);
      }

      return history.push(locationState);
    },
  });
};

export default addCartListeners;
